import { WYSIWYG } from 'libs/wordpress/types';

type WysiwygNode = Record<string, any> | string;
type WysiwygContent = WysiwygNode | WysiwygNode[];

const tokenRegExp = /\{\{\s*([A-Z0-9_]+)\s*\}\}/g;

/**
 * Splits a text string into text parts and a pseudo-tag object for every {{TOKEN}} found.
 * The pseudo-tag is shaped like a WYSIWYG tag so that Wysiwyg can render it through tagComponents.
 *
 * @param {string} text - Text that may contain one or more {{TOKEN}}.
 * @param {string} tag - Tag name given to the pseudo-tag objects.
 */
const splitTextOnTokens = (text: string, tag: string): WysiwygContent => {
    const parts: WysiwygNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    tokenRegExp.lastIndex = 0;

    while ((match = tokenRegExp.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }

        parts.push({ tag, attributes: { 'data-token': match[1] }, content: [match[1]] });
        lastIndex = match.index + match[0].length;
    }

    // Keep the original string when there is nothing to replace
    if (!parts.length) {
        return text;
    }

    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    return parts;
};

const transformContent = (content: any, tag: string): any => {
    if (typeof content === 'string') {
        return splitTextOnTokens(content, tag);
    }

    // Concat instead of map to keep a split string on the same level, otherwise the extra
    // nesting level makes Wysiwyg generate duplicate keys for its children.
    if (Array.isArray(content)) {
        return content.reduce((acc, part) => acc.concat(transformContent(part, tag)), []);
    }

    if (content && typeof content === 'object') {
        return { ...content, content: transformContent(content.content, tag) };
    }

    return content;
};

/**
 * Replaces every {{TOKEN}} in WYSIWYG data with a pseudo-tag that can be rendered by a
 * custom component. Pass the same tag name to Wysiwyg through both validTags and tagComponents.
 *
 * Ex:
 *  data -> ['One or more products is covered by {{GARAN}}.']
 *  tag -> 'harmonisationToken'
 *  returns -> [['One or more products is covered by ', { tag: 'harmonisationToken', attributes: { 'data-token': 'GARAN' }, content: ['GARAN'] }, '.']]
 *
 * @param {WYSIWYG|string} data - WYSIWYG data from the CMS.
 * @param {string} tag - Tag name given to the pseudo-tag objects.
 */
export const injectWysiwygTokens = (data: WYSIWYG | string, tag = 'wysiwygToken'): WYSIWYG => {
    // Mirror the legacy string handling in Wysiwyg so that each line stays its own row
    const rows = typeof data === 'string' ? data.split('\n') : data;

    return Array.isArray(rows) ? rows.map(row => transformContent(row, tag)) : rows;
};
